import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { profileKeys, updateProfile, type Profile } from '@entities/Profile';
import { useAuthStore } from '@entities/User';
import { trackEvent } from '@shared/analytics';
import { campaignSearch } from '@shared/lib/campaignParams';
import {
  fetchOnboardingStarter,
  finishOnboarding,
  type FinishOnboardingPayload,
  type FinishOutcome,
} from '../api/onboardingApi';
import {
  reminderFields,
  reminderProfileFields,
  type OnboardingStep,
  type ReminderChoice,
  type ReminderFields,
} from './flow';

export const onboardingKeys = {
  starter: ['onboarding', 'starter'] as const,
};

/** Стартовый модуль и альтернативы — одинаковые для всех, кешируем надолго. */
export function useOnboardingStarterQuery() {
  return useQuery({
    queryKey: onboardingKeys.starter,
    queryFn: fetchOnboardingStarter,
    staleTime: 10 * 60_000,
    retry: 1,
  });
}

/** Поля профиля, которые меняет выбор напоминаний на итоге. */
function reminderPatch(fields: ReminderFields): Partial<Profile> {
  return {
    ...(fields.reminders_off ? { is_notification: false } : {}),
    ...(fields.reminder_hour !== undefined ? { reminder_hour: fields.reminder_hour } : {}),
  };
}

/**
 * Профиль в кеше помечается пройденным до смены стадии: иначе AppGate на главной увидел бы старый
 * `completed_onboarding: false` и вернул ученика в знакомство.
 */
function markOnboardingFinished(qc: QueryClient, patch: Partial<Profile>) {
  qc.setQueryData<Profile | undefined>(profileKeys.current, (old) =>
    old ? { ...old, ...patch, completed_onboarding: true } : old,
  );
  void qc.invalidateQueries({ queryKey: profileKeys.current });
}

/** Завершить знакомство и уйти на главную («Пропустить»). */
export function useFinishOnboardingMutation() {
  const qc = useQueryClient();
  const setStage = useAuthStore((s) => s.setStage);

  return useMutation<FinishOutcome, Error, FinishOnboardingPayload>({
    mutationFn: finishOnboarding,
    onSuccess: (_, variables) => {
      markOnboardingFinished(qc, reminderPatch(variables));
      setStage('app');
    },
  });
}

/**
 * «На главную» на итоге (B5, 2026-10-20). Знакомство обычно уже засчитано при входе на итог
 * (`finishedEarly`) — тогда выбор времени уходит в `PUT /profile`; иначе — finish-onboarding с
 * выбором, а если сервер ответит, что знакомство уже завершено (вторая вкладка, ранний запрос дошёл
 * без ответа), выбор всё равно сохраняется через профиль.
 */
export function useLeaveOnboardingResultMutation() {
  const qc = useQueryClient();
  const setStage = useAuthStore((s) => s.setStage);

  return useMutation<Partial<Profile>, Error, { choice: ReminderChoice; finishedEarly: boolean }>({
    mutationFn: async ({ choice, finishedEarly }) => {
      if (!finishedEarly) {
        const fields = reminderFields(choice);
        if ((await finishOnboarding({ module_ids: [], ...fields })) === 'finished') return reminderPatch(fields);
      }
      const profileFields = reminderProfileFields(choice);
      if (!profileFields) return {};
      const fresh = await updateProfile(profileFields);
      return { is_notification: fresh.is_notification, reminder_hour: fresh.reminder_hour };
    },
    onSuccess: (patch) => {
      markOnboardingFinished(qc, patch);
      setStage('app');
    },
  });
}

/** `onboarding_step_viewed {step}` — один раз на показ шага (ref переживает повтор эффекта в StrictMode). */
export function useOnboardingStepViewed(step: OnboardingStep) {
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    trackEvent('onboarding_step_viewed', { step });
  }, [step]);
}

/**
 * Query следующего шага: свой `module` и метки кампании текущего адреса — их, как и прежде,
 * донесёт до главной AfterSignInRedirect (campaignSearch), остальные параметры не идут.
 */
export function onboardingStepSearch(currentSearch: string, moduleId?: string): string {
  const labels = campaignSearch(currentSearch).slice(1);
  const parts = [moduleId ? `module=${encodeURIComponent(moduleId)}` : '', labels].filter(Boolean);
  return parts.length ? `?${parts.join('&')}` : '';
}
